import React from 'react';
import styled, { css } from 'styled-components';

import { Link } from 'components/core';
import Imgix from 'shared/components/imgix';

import { useMeshGradientColors } from './use-mesh-gradient-colors';
import { overlayButtonStyles } from './buy-it-again-shared-styles';

type BuyItAgainPromoCardProps = {
  href: string;
  onClick: () => void;
  logoImage?: string | null;
};

export function BuyItAgainPromoCard({ href, onClick, logoImage }: BuyItAgainPromoCardProps): JSX.Element {
  const meshColors = useMeshGradientColors(logoImage);

  return (
    <CardShell data-testid='buy-it-again-promo-card'>
      <MeshGradient $colors={meshColors} />

      {logoImage && (
        <LogoBackground>
          <Imgix
            imgixParams={{ auto: 'format,compress', cs: 'srgb', fit: 'clip' }}
            width={300}
            height={300}
            htmlAttributes={{ alt: '', loading: 'lazy' }}
            src={logoImage}
          />
        </LogoBackground>
      )}

      {/* Sits on top so its blur/tint softens the mesh gradient and logo beneath it,
          while keeping the heading and CTA text crisp and legible. */}
      <TextOverlay>
        <Heading>Reorder your favorites</Heading>
        <CtaLink href={href} onClick={onClick}>
          Past orders
        </CtaLink>
      </TextOverlay>
    </CardShell>
  );
}

// No hover treatment here — unlike the tiles beside it, the card body isn't itself a link
// (only the "Past orders" CTA is), so it shouldn't imply whole-card interactivity.
const CardShell = styled.div`
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  height: 152px;
  overflow: hidden;
  position: relative;
  width: 300px;

  @media screen and (min-width: 1110px) {
    height: 198px;
    width: 380px;
  }
`;

const MeshGradient = styled.div<{ $colors: string[] | null }>`
  background-color: var(--dcm-buy-it-again-promo-bg, ${({ theme }) => theme.colors.grey[95]});
  bottom: 0;
  filter: saturate(190%) contrast(105%);
  left: 0;
  position: absolute;
  right: 0;
  top: 0;

  ${({ $colors }) =>
    $colors &&
    css`
      background-image: radial-gradient(at 12% 20%, ${$colors[0]} 0%, transparent 78%),
        radial-gradient(at 88% 15%, ${$colors[1]} 0%, transparent 78%),
        radial-gradient(at 50% 50%, ${$colors[2]} 0%, transparent 85%),
        radial-gradient(at 18% 85%, ${$colors[3]} 0%, transparent 78%),
        radial-gradient(at 90% 82%, ${$colors[4]} 0%, transparent 78%);
    `}
`;

// Base background assumes NO backdrop-filter support (higher-opacity flat tint, for contrast
// on its own). Browsers that do support it get the lighter, softer glass look instead — the
// blur itself provides the separation from the mesh gradient/logo behind it.
const TextOverlay = styled.div`
  align-items: flex-start;
  background-color: var(--dcm-buy-it-again-promo-glass-bg, rgba(255, 255, 255, 0.22));
  border: 1px solid var(--dcm-buy-it-again-promo-glass-border, rgba(255, 255, 255, 0.15));
  bottom: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
  justify-content: center;
  left: 0;
  padding: 20px;
  position: absolute;
  right: 0;
  top: 0;

  @supports (backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)) {
    background-color: var(--dcm-buy-it-again-promo-glass-bg-blurred, rgba(255, 255, 255, 0.08));
    backdrop-filter: blur(2px) saturate(105%);
    -webkit-backdrop-filter: blur(2px) saturate(105%);
  }
`;

const Heading = styled.p`
  color: var(--dcm-buy-it-again-promo-heading-color, #121516);
  font-size: 20px;
  font-weight: 800;
  line-height: ${24 / 20};
  max-width: 190px;
  text-shadow: var(--dcm-buy-it-again-promo-heading-text-shadow, 0 1px 2px rgba(0, 0, 0, 0.1));

  @media screen and (min-width: 1110px) {
    font-size: 24px;
  }
`;

// Styled directly on the anchor (not a nested span) so hover/focus land on the real interactive
// element — same solid-white pill look as the carousel's other overlay buttons (quick-add,
// "View similar"), just inline instead of absolutely positioned.
const CtaLink = styled(Link)`
  ${overlayButtonStyles}
  border-radius: 9999px;
  color: var(--dcm-filter-text-color, #121516);
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  padding: 8px 16px;
`;

// The dispensary logo itself, scaled up as a soft background element behind the frosted-glass
// text overlay — sits above the mesh gradient but below TextOverlay, so its blur softens it.
// Kept to the right side of the card so it doesn't collide with the left-aligned heading text.
const LogoBackground = styled.div`
  align-items: center;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  left: 0;
  padding-right: 16px;
  position: absolute;
  right: 0;
  top: 0;

  img {
    height: 60%;
    object-fit: contain;
    width: auto;
  }

  @media screen and (min-width: 1110px) {
    padding-right: 24px;
  }
`;
